// 基础模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import {
  BaseModuleState,
  BaseModuleActions,
  CacheConfig,
  DEFAULT_CACHE_CONFIG,
} from "../types";

// 基础缓存类
class BaseCache {
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> =
    new Map();
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
  }

  // 设置缓存
  setCache(key: string, data: any, ttl?: number): void {
    const actualTTL = ttl ?? this.config.defaultTTL;

    // 检查缓存大小限制
    if (this.cache.size >= this.config.maxSize) {
      // 删除最旧的缓存项
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: actualTTL,
    });
  }

  // 获取缓存
  getCache(key: string): any {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);

      return null;
    }

    return item.data;
  }

  // 清除缓存
  clearCache(pattern?: string): void {
    if (pattern) {
      const keysToDelete = [];
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  // 检查缓存是否存在
  hasCache(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // 获取所有缓存键
  getCacheKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  // 获取缓存大小
  getCacheSize(): number {
    return this.cache.size;
  }

  // 缓存优先的 API 调用
  async cacheFirst<T>(
    key: string,
    apiCall: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // 先检查缓存
    const cached = this.getCache(key);
    if (cached !== null) {
      return cached;
    }

    // 缓存未命中，调用 API
    try {
      const data = await apiCall();
      this.setCache(key, data, ttl);
      return data;
    } catch (error) {
      if (this.config.enableLogging) {
        console.error(`[Cache] API call failed for key: ${key}`, error);
      }
      throw error;
    }
  }
}

// 创建基础缓存 store
export const createBaseCacheStore = (config: Partial<CacheConfig> = {}) => {
  const cacheConfig = { ...DEFAULT_CACHE_CONFIG, ...config };
  const baseCache = new BaseCache(cacheConfig);

  return createWithEqualityFn<BaseModuleState & BaseModuleActions>()(
    subscribeWithSelector(set => ({
      // 初始状态
      cache: new Map(),

      // 缓存相关方法 - 委托给 BaseCache 类
      setCache: (key, data, ttl) => {
        baseCache.setCache(key, data, ttl);
        set({ cache: new Map(baseCache["cache"]) });
      },

      getCache: key => {
        return baseCache.getCache(key);
      },

      clearCache: pattern => {
        baseCache.clearCache(pattern);
        set({ cache: new Map(baseCache["cache"]) });
      },

      hasCache: key => {
        return baseCache.hasCache(key);
      },

      getCacheKeys: () => {
        return baseCache.getCacheKeys();
      },

      getCacheSize: () => {
        return baseCache.getCacheSize();
      },

      cacheFirst: async (key, apiCall, ttl) => {
        return baseCache.cacheFirst(key, apiCall, ttl);
      },
    }))
  );
};

// 默认基础缓存 store
export const useBaseCacheStore = createBaseCacheStore();

// 导出 BaseCache 类供其他模块使用
export { BaseCache };
